'use client'

import { ArrowRight, ArrowLeft } from './icons'
import { cn } from '@/lib/utils'

type ProductPagePaginationProps = {
  currentPage: number
  setCurrentPage: (page: number) => void
  totalPages: number
}

export const ListingPagePagination: React.FC<ProductPagePaginationProps> = ({
  currentPage,
  setCurrentPage,
  totalPages,
}) => {
  const getPageNumbers = () => {
    const MAX_VISIBLE_PAGES = 5
    const ELLIPSIS = '...'

    if (totalPages <= MAX_VISIBLE_PAGES) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }

    const pages: (number | string)[] = []

    let startPage = Math.max(currentPage - 2, 1)
    const endPage = Math.min(startPage + 4, totalPages)

    if (endPage === totalPages) {
      startPage = Math.max(endPage - 4, 1)
    }

    if (startPage > 1) {
      pages.push(1)
      if (startPage > 2) pages.push(ELLIPSIS)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push(ELLIPSIS)
      pages.push(totalPages)
    }

    return pages
  }

  return (
    <nav className="mt-4 mb-10 lg:mt-4 lg:mb-40 flex justify-center items-center md:space-x-2 space-x-0.5 bg-white">
      <button
        className="w-8 md:w-10 h-8 md:h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:opacity-50"
        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
        disabled={currentPage === 1}
      >
        <ArrowLeft />
        <span className="sr-only">前のページ</span>
      </button>

      <div className="flex items-center md:space-x-6 space-x-4">
        {getPageNumbers().map((page, i) =>
          typeof page === 'number' ? (
            <button
              key={i}
              onClick={() => setCurrentPage(page)}
              className={cn(
                'w-8 md:w-9 h-8 md:h-9 rounded-full flex items-center justify-center text-lg font-normal transition-colors',
                currentPage === page
                  ? 'bg-black text-white hover:bg-black/90'
                  : 'hover:bg-gray-100'
              )}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          ) : (
            <span
              key={i}
              className="w-8 md:w-10 h-8 md:h-10 flex items-center justify-center"
            >
              {page}
            </span>
          )
        )}
      </div>

      <button
        className="w-8 md:w-10 h-8 md:h-10 flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:opacity-50"
        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        <ArrowRight />
        <span className="sr-only">次のページ</span>
      </button>
    </nav>
  )
}

export default ListingPagePagination
